/**
 * Figura principal (comissao do periodo, com variacao) e os quatro cartoes
 * por status, que tambem sao ATALHOS: clicar filtra o extrato por aquele
 * status e rola ate ele.
 *
 * Decisoes nao obvias:
 *  1. UM so destaque na pagina, e e a comissao ATIVA (a faturar + faturada
 *     + paga): e o faturamento do Converte Facil no recorte. A revertida fica
 *     no quarto cartao -- somar os quatro NAO da a figura principal, e o
 *     rotulo "revertida" avisa isso.
 *  2. A "taxa efetiva" (comissao / recuperado) aparece no destaque: com
 *     empresas de percentuais diferentes, e ela que diz quanto de cada real
 *     recuperado vira receita do Converte Facil.
 *  3. Os cartoes de status sao BOTOES com `aria-pressed`: o estado do filtro
 *     e lido pelo leitor de tela, e o foco e o do botao (teclado).
 *  4. Nenhum centavo chega aqui sem passar por lib/format.
 */

import { Sparkline } from '../../../components/Sparkline';
import { Icon } from '../../../components/ui';
import { formatBRLFromCents, formatCount, formatRatio } from '../../../lib/format';
import type { CommissionSummary, ConversionStatus, PeriodTotals, StatusFilter } from '../types';
import { ORDEM_STATUS, STATUS_CONVERSAO, plural } from '../vocabulario';
import { Delta } from './Delta';

const ROTULO_CARTAO: Readonly<Record<ConversionStatus, string>> = {
  a_faturar: 'A faturar',
  faturada: 'Faturada',
  paga: 'Paga',
  revertida: 'Comissão revertida',
};

const TOM_DO_ICONE: Readonly<Record<ConversionStatus, string>> = {
  a_faturar: 'bg-status-warning/15 text-status-warning',
  faturada: 'bg-info/10 text-info',
  paga: 'bg-status-good/10 text-status-good',
  revertida: 'bg-status-critical/10 text-status-critical',
};

export function CartoesComissao({
  summary,
  previous,
  tendencia,
  rotuloTendencia,
  periodo,
  escopo,
  statusAtivo,
  onStatus,
}: {
  readonly summary: CommissionSummary;
  readonly previous: PeriodTotals | null;
  /** Comissao por balde (dia, semana ou mes), para o traco de tendencia. */
  readonly tendencia: readonly number[];
  readonly rotuloTendencia: string;
  /** Descricao da janela do cabecalho ("Últimos 30 dias"). */
  readonly periodo: string;
  /** "Todas as empresas" ou o nome da empresa filtrada. */
  readonly escopo: string;
  /** Status filtrado no extrato agora (o cartao correspondente fica marcado). */
  readonly statusAtivo: StatusFilter;
  readonly onStatus: (status: ConversionStatus) => void;
}): JSX.Element {
  const porVenda = summary.conversionCount === 0 ? null : Math.round(summary.commissionCents / summary.conversionCount);

  return (
    <>
      <article className="relative overflow-hidden rounded-2xl border border-line bg-hero px-6 py-6 text-white shadow-float sm:px-8 sm:py-7">
        <div aria-hidden="true" className="pointer-events-none absolute -right-16 -top-20 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
        <div className="relative flex flex-wrap items-end justify-between gap-6">
          <div className="min-w-0">
            <h2 className="flex items-center gap-2 text-sm font-medium text-white/80">
              <Icon name="comissao" size="sm" />
              Comissão do período
            </h2>
            <p className="mt-2 text-4xl font-semibold tracking-tight sm:text-6xl">{formatBRLFromCents(summary.commissionCents)}</p>
            <Delta atual={summary.commissionCents} anterior={previous === null ? null : previous.commissionCents} tipo="dinheiro" monocromatico className="mt-2 text-sm text-white/85" />
            <p className="mt-2 text-sm text-white/75">
              {formatCount(summary.conversionCount)} {plural(summary.conversionCount, 'conversão ativa', 'conversões ativas')} ·{' '}
              {formatBRLFromCents(summary.recoveredAmountCents)} recuperados para as empresas · {escopo} · {periodo}
            </p>
            <p className="mt-1 text-xs text-white/70">
              Taxa efetiva <strong className="font-semibold text-white">{formatRatio(summary.commissionCents, summary.recoveredAmountCents)}</strong> do recuperado
              {porVenda === null ? null : (
                <>
                  {' '}
                  · <strong className="font-semibold text-white">{formatBRLFromCents(porVenda)}</strong> por venda
                </>
              )}
            </p>
          </div>
          <div className="shrink-0 text-white">
            <Sparkline values={tendencia} label={rotuloTendencia} className="h-12 w-36 sm:h-14 sm:w-52" />
          </div>
        </div>
      </article>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" role="group" aria-label="Comissão por status. Clique para filtrar o extrato">
        {ORDEM_STATUS.map((status) => {
          const balde = summary.byStatus[status];
          const info = STATUS_CONVERSAO[status];
          const ativo = statusAtivo === status;
          return (
            <button
              key={status}
              type="button"
              aria-pressed={ativo}
              onClick={() => onStatus(status)}
              title={`${info.hint}. Clique para ver no extrato.`}
              className={`group flex flex-col gap-2 rounded-2xl border bg-surface px-5 py-4 text-left shadow-soft transition-all duration-150 hover:shadow-float active:scale-[0.99] ${
                ativo ? 'border-brand ring-1 ring-brand/40' : 'border-line hover:border-brand/40'
              }`}
            >
              <span className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2 text-sm font-medium text-ink-muted">
                  <span aria-hidden="true" className={`grid h-7 w-7 place-items-center rounded-lg ${TOM_DO_ICONE[status]}`}>
                    <Icon name={info.icon} size="sm" />
                  </span>
                  {ROTULO_CARTAO[status]}
                </span>
                <span className={`text-[11px] font-medium ${ativo ? 'text-brand' : 'text-ink-subtle group-hover:text-brand'}`}>{ativo ? 'Filtrando' : 'Ver no extrato'}</span>
              </span>
              <span className={`text-2xl font-semibold tracking-tight sm:text-3xl ${status === 'revertida' && balde.count > 0 ? 'text-status-critical' : 'text-ink'}`}>
                {formatBRLFromCents(balde.commissionCents)}
              </span>
              <span className="text-xs text-ink-subtle">
                {formatCount(balde.count)} {plural(balde.count, 'conversão', 'conversões')} · {info.hint}
              </span>
              {status === 'revertida' ? (
                <Delta atual={balde.commissionCents} anterior={previous === null ? null : previous.reversedCommissionCents} tipo="dinheiro" sentido="menor_melhor" />
              ) : null}
            </button>
          );
        })}
      </div>
    </>
  );
}
